import { ChevronDown, ChevronRight, Layers, MousePointer, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { useBuilderStore } from "@/lib/builder/builder-store";
import { getMaterial } from "@/lib/builder/materials";
import type { ComponentNode } from "@shared/types";

interface LayerItemProps {
  node: ComponentNode;
  depth?: number;
}

function LayerItem({ node, depth = 0 }: LayerItemProps) {
  const { selectedNodeId, selectNode, deleteNode } = useBuilderStore();
  const material = getMaterial(node.type);
  const isSelected = selectedNodeId === node.id;
  const isContainer = material?.allowChildren ?? false;
  const children = node.children || [];
  const [expanded, setExpanded] = React.useState(true);

  return (
    <div className="select-none">
      <div
        className={cn(
          "flex cursor-pointer items-center gap-1 rounded-sm py-1 pr-1 text-xs hover:bg-accent",
          isSelected && "bg-primary/10 font-medium text-primary hover:bg-primary/10",
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
        onClick={() => selectNode(node.id)}
      >
        {isContainer && children.length > 0 && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
        )}
        {!isContainer && <span className="w-3" />}
        <span className="truncate" title={material?.label || node.type}>
          {material?.label || node.type}
        </span>
        {isSelected && (
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-5 w-5 text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              deleteNode(node.id);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
      {isContainer && expanded && (
        <div>
          {children.map((child) => (
            <LayerItem key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function LayersPanel() {
  const { schema, activePageId, selectedNodeId, selectNode } = useBuilderStore();
  const page = schema.pages.find((p) => p.id === activePageId);
  const [filter, setFilter] = React.useState("");

  const visibleIds = React.useMemo(() => {
    if (!filter.trim() || !page) return new Set<string>();
    const term = filter.toLowerCase();
    const ids = new Set<string>();
    const visit = (node: ComponentNode) => {
      const material = getMaterial(node.type);
      const label = (material?.label || node.type).toLowerCase();
      if (label.includes(term)) {
        ids.add(node.id);
      }
      node.children?.forEach(visit);
    };
    visit(page.layout);
    return ids;
  }, [filter, page]);

  if (!page) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <MousePointer className="mb-2 h-4 w-4" />
        暂无页面
      </div>
    );
  }

  const rootMaterial = getMaterial(page.layout.type);

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase text-muted-foreground">页面图层</span>
      </div>
      <Input
        placeholder="搜索图层..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="h-8 text-xs"
      />
      <div className="flex-1 overflow-auto">
        <div
          className={cn(
            "flex cursor-pointer items-center gap-1 rounded-sm py-1 pr-1 text-xs hover:bg-accent",
            selectedNodeId === page.layout.id && "bg-primary/10 font-medium text-primary",
          )}
          onClick={() => selectNode(page.layout.id)}
        >
          <span className="w-3" />
          <span className="truncate">{rootMaterial?.label || page.layout.type}</span>
        </div>
        {page.layout.children?.map((child) =>
          filter.trim() && !visibleIds.has(child.id) ? null : (
            <LayerItem key={child.id} node={child} depth={1} />
          ),
        )}
      </div>
    </div>
  );
}
